import { CATEGORY_DEPARTMENT_PRESCRIPTIONS } from '../../../constants';
import { PERMISSION } from '../../../components/investigation/share/user_roles';

// SHER-570: the API only allows DELETE within 24h of creation (errorCode 3 after
// that). Delete and annul are complementary and time-exclusive: delete corrects a
// fresh error within the window, annul discontinues a committed ordonnance after
// it. Showing delete past the window (or annul inside it) would offer a dead-end
// or redundant action, so both icons are gated on the window too.
const DELETION_WINDOW_MS = 24 * 60 * 60 * 1000;

export function isWithinDeletionWindow(createdAt?: Date | string | null, now: number = Date.now()): boolean {
    // No timestamp yet (brand-new / unsynced row): treat as fresh → deletable.
    if (!createdAt) {
        return true;
    }
    const created = new Date(createdAt).getTime();
    if (Number.isNaN(created)) {
        return true;
    }
    return now - created <= DELETION_WINDOW_MS;
}

// Table-level (window-agnostic) gate: should this prescriptions table offer the
// delete/annul row actions at all? The per-row window logic lives in the two
// functions below; this just avoids pushing the actions on unrelated tables.
export function canUsePrescriptionRowActions({
    category,
    permissions,
}: {
    category: number;
    permissions?: string[];
}): boolean {
    if (category !== CATEGORY_DEPARTMENT_PRESCRIPTIONS) {
        return false;
    }
    return Boolean(
        permissions?.includes(PERMISSION.DELETE_SUBMISSION) ||
        permissions?.includes(PERMISSION.STOP_TREATMENT),
    );
}

export function canDeletePrescriptionFromTable({
    category,
    permissions,
    recordType,
    createdAt,
    now,
}: {
    category: number;
    permissions?: string[];
    recordType?: string;
    createdAt?: Date | string | null;
    now?: number;
}): boolean {
    if (recordType === 'stay') {
        return false;
    }
    if (category !== CATEGORY_DEPARTMENT_PRESCRIPTIONS) {
        return false;
    }
    // Delete is only offered inside the 24h correction window.
    if (!isWithinDeletionWindow(createdAt, now)) {
        return false;
    }
    return Boolean(permissions?.includes(PERMISSION.DELETE_SUBMISSION));
}

/**
 * SHER-570: whether the "arrêter l'ordonnance" (annul) action is available for a
 * prescription row. Annul discontinues a legitimate ordonnance in place (stays
 * visible, marked stopped) — unlike delete, which is for fresh errors. Gated by
 * STOP_TREATMENT (the same permission that governs the inpatient stop), scoped to
 * the outpatient prescriptions table.
 */
export function canAnnulPrescriptionFromTable({
    category,
    permissions,
    recordType,
    createdAt,
    now,
}: {
    category: number;
    permissions?: string[];
    recordType?: string;
    createdAt?: Date | string | null;
    now?: number;
}): boolean {
    if (recordType === 'stay') {
        return false;
    }
    if (category !== CATEGORY_DEPARTMENT_PRESCRIPTIONS) {
        return false;
    }
    // Annul only applies AFTER the delete window: inside it, a fresh error is
    // deleted, not annulled.
    if (isWithinDeletionWindow(createdAt, now)) {
        return false;
    }
    return Boolean(permissions?.includes(PERMISSION.STOP_TREATMENT));
}
